import { useQueryClient } from "@tanstack/react-query";
import { useGrowl } from "PFApp/use_growl";
import NotFoundComponent from "PFComponents/not_found/not_found";
import usePageWrapper from "PFCore/helpers/use_page_wrapper";
import { INTEREST_QUESTIONNAIRES_KEY } from "PFCore/hooks/queries/common/use_interest_questionnaires";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import { useTemplatesQuery, useTemplateUpdate } from "../services/queries";
import { BoolFieldName } from "../types";
import InterestQuestionsPage from "./page";
import { ToggleAction, ToggleIdentifier } from "./types";

const InterestQuestionsController = (): JSX.Element => {
  const { t } = useTranslation(["interestQuestions", "translation"]);
  const growl = useGrowl();
  const queryClient = useQueryClient();
  const [changedToggle, setChangedToggle] = useState<ToggleIdentifier | null>(null);

  const WrappedInterestQuestionsPage = usePageWrapper(InterestQuestionsPage);

  const { data, isLoading, refetch, error } = useTemplatesQuery({
    onSettled: () => {
      if (changedToggle) {
        setChangedToggle(null);
      }
    }
  });

  const { mutate: updateTemplate } = useTemplateUpdate({
    onSuccess: () => {
      growl({
        message: t("interestQuestions:templates.toggleSuccess"),
        ttl: 2000
      });
      queryClient.invalidateQueries({
        queryKey: INTEREST_QUESTIONNAIRES_KEY,
        exact: true
      });
    },
    onSettled: () => {
      refetch();
    }
  });

  const handleToggleClick = (action: ToggleAction) => {
    setChangedToggle({
      rowId: action.rowId,
      columnName: action.columnName
    });
    const autoToggleValues: { [key in BoolFieldName]?: boolean } = {};
    if (action.columnName === "showQuestions" && action.newValue === false) {
      autoToggleValues.allowRmToEdit = false;
      autoToggleValues.allowRmToEnable = false;
    }
    updateTemplate({ id: action.rowId, data: { [action.columnName]: action.newValue, ...autoToggleValues } });
  };

  return (
    <WrappedInterestQuestionsPage
      loading={isLoading}
      errors={error ? [error] : []}
      errorComponent={
        <NotFoundComponent
          message={t("interestQuestions:notFoundMessage")}
          buttonMessage={t("translation:comeBackLater")}
          buttonUrl="/admin/interest_questions"
        />
      }
      tableRows={data?.entries || []}
      onToggleClick={handleToggleClick}
      changedToggle={changedToggle}
    />
  );
};

export default InterestQuestionsController;
